import React, { forwardRef, useImperativeHandle, useState } from "react";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import { SERVICES } from "../data/content";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const initialState = { name: "", phone: "", service: "", comment: "" };

const RequestForm = forwardRef((props, ref) => {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useImperativeHandle(ref, () => ({
    prefillService: (slug) => {
      setDone(false);
      setForm((f) => ({ ...f, service: slug || "" }));
    },
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      setForm(initialState);
      toast.success("Заявка принята", { description: "Свяжемся с вами в ближайшее время." });
    } catch (err) {
      console.error(err);
      toast.error("Не удалось отправить заявку. Позвоните нам напрямую.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="request" className="section-y bg-burgundy text-cream" data-testid="request-section">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <h2 className="font-heading text-5xl md:text-7xl text-cream heading-tight">
            Оставьте номер.
            <br />
            <span className="accent text-gold-light">Мы перезвоним.</span>
          </h2>
          <p className="mt-8 lede text-cream/90 max-w-md">
            Опишите задачу или просто оставьте телефон. Перезваниваем в течение часа в рабочее время.
          </p>

          <ul className="mt-12 space-y-3 text-cream/90 text-base font-medium">
            <li>✦ Бесплатная оценка стоимости и сроков</li>
            <li>✦ Доставка по Брянску — от 350 ₽</li>
            <li>✦ Гарантия на каждую услугу</li>
          </ul>
        </div>

        <div className="lg:col-span-7">
          <div className="border border-cream/30 bg-burgundy-deep/40 p-6 md:p-10">
            {done ? (
              <div className="text-center py-12" data-testid="request-success">
                <CheckCircle2 size={48} strokeWidth={1.25} className="mx-auto text-gold" />
                <h3 className="mt-6 font-heading text-3xl">Заявка принята!</h3>
                <p className="mt-3 text-cream/80 max-w-md mx-auto">
                  Спасибо. Мы перезвоним вам в ближайшее время и обсудим детали задачи.
                </p>
                <button onClick={() => setDone(false)} className="btn-ghost mt-8 !text-cream !border-cream hover:!bg-cream hover:!text-burgundy" data-testid="request-new-btn">
                  Отправить ещё одну заявку
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-testid="request-form">
                <div className="md:col-span-1">
                  <label className="block text-xs uppercase tracking-[0.22em] text-cream/70 mb-2">Имя</label>
                  <input
                    type="text"
                    placeholder="Иван"
                    className="input-vintage !text-cream !placeholder-cream/40 !border-cream/40 focus:!border-gold"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    data-testid="request-input-name"
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-xs uppercase tracking-[0.22em] text-cream/70 mb-2">Телефон</label>
                  <input
                    type="tel"
                    placeholder="+7 ___ ___ __ __"
                    className="input-vintage !text-cream !placeholder-cream/40 !border-cream/40 focus:!border-gold"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    required
                    data-testid="request-input-phone"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.22em] text-cream/70 mb-2">Услуга</label>
                  <select
                    className="input-vintage !text-cream !border-cream/40 focus:!border-gold bg-burgundy-deep/0"
                    value={form.service}
                    onChange={(e) => setForm({ ...form, service: e.target.value })}
                    data-testid="request-input-service"
                  >
                    <option value="" className="text-ink">— Выберите услугу —</option>
                    {SERVICES.map((s) => (
                      <option key={s.slug} value={s.slug} className="text-ink">{s.title}</option>
                    ))}
                    <option value="other" className="text-ink">Другое / уточнить</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.22em] text-cream/70 mb-2">Комментарий</label>
                  <textarea
                    rows={4}
                    placeholder="Что с обувью? Какой результат хочется?"
                    className="input-vintage !text-cream !placeholder-cream/40 !border-cream/40 focus:!border-gold resize-none"
                    value={form.comment}
                    onChange={(e) => setForm({ ...form, comment: e.target.value })}
                    data-testid="request-input-comment"
                  />
                </div>
                <div className="md:col-span-2 flex flex-col md:flex-row md:items-center gap-4 md:justify-between mt-2">
                  <p className="text-xs text-cream/60 max-w-sm">
                    Отправляя заявку, вы соглашаетесь на обработку персональных данных.
                  </p>
                  <button type="submit" disabled={submitting} className="btn-primary !bg-gold !text-burgundy !border-gold hover:!bg-gold-hover hover:!border-gold-hover" data-testid="request-submit-btn">
                    {submitting ? "Отправляем…" : "Отправить заявку"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
});

RequestForm.displayName = "RequestForm";

export default RequestForm;
